"use client";
import React, { useState, useEffect, useRef } from "react";
import Icon from "@/components/ui/AppIcon";

export default function KontaktSection() {
  const [form, setForm] = useState({ name: "", email: "", telefon: "", was: "", nachricht: "" });
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add("revealed");
        });
      },
      { threshold: 0.1 }
    );
    sectionRef.current?.querySelectorAll(".reveal-hidden").forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const res = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });

      if (res.ok) {
        setSent(true);
      } else {
        const data = await res.json();
        setError(data.error || "Fehler beim Senden. Bitte versuche es erneut.");
      }
    } catch {
      setError("Verbindungsfehler. Bitte versuche es erneut.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="kontakt" ref={sectionRef} className="relative py-28 lg:py-36 px-6 lg:px-12 bg-[#0A0A14] overflow-hidden">
      <div className="orb w-[500px] h-[500px] bg-violet-500/8 top-0 right-0" />
      <div className="orb w-[300px] h-[300px] bg-blue-500/6 bottom-0 left-0" />

      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-16">
        {/* Left */}
        <div className="lg:col-span-5 reveal-hidden">
          <div className="section-label mb-4">Kontakt</div>
          <h2 className="font-display font-extrabold text-[clamp(2rem,4vw,3rem)] text-white leading-tight mb-6">
            Lass uns{" "}
            <span className="gradient-text">loslegen</span>
          </h2>
          <p className="text-white/45 leading-relaxed mb-10 text-sm">
            Schreib mir einfach – ich melde mich so schnell wie möglich zurück. Kein Formular-Stress, kein langer Warteprozess.
          </p>

          {/* Contact Methods */}
          <div className="space-y-4 mb-10">
            {[
              {
                icon: "ChatBubbleOvalLeftEllipsisIcon",
                label: "WhatsApp / SMS",
                value: "+41 79 612 21 52",
                color: "text-green-400",
                href: "https://wa.me/41796122152",
              },
              {
                icon: "EnvelopeIcon",
                label: "E-Mail",
                value: "alex.motie@icloud.com",
                color: "text-blue-400",
                href: "mailto:alex.motie@icloud.com",
              },
              {
                icon: "MapPinIcon",
                label: "Standort",
                value: "Schweiz – Für maximale Sicherheit selbst abholen",
                color: "text-violet-400",
                href: null,
              },
            ].map((c) => (
              <div key={c.label} className="flex items-center gap-4 p-4 rounded-lg bg-white/[0.03] border border-white/[0.05] hover:border-violet-500/20 transition-colors">
                <div className="w-10 h-10 rounded-lg bg-white/[0.05] flex items-center justify-center flex-shrink-0">
                  <Icon name={c.icon as any} size={18} className={c.color} />
                </div>
                <div>
                  <div className="font-mono text-[10px] text-white/30 uppercase tracking-widest">{c.label}</div>
                  {c.href ? (
                    <a href={c.href} className="text-sm text-white/70 font-medium mt-0.5 hover:text-white transition-colors block">{c.value}</a>
                  ) : (
                    <div className="text-sm text-white/70 font-medium mt-0.5">{c.value}</div>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Response time */}
          <div className="flex items-center gap-3 p-4 rounded-lg border border-green-500/15 bg-green-500/[0.04]">
            <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
            <span className="text-xs text-green-400/80">Antwortzeit in der Regel unter 24 Stunden</span>
          </div>
        </div>

        {/* Form */}
        <div className="lg:col-span-7 reveal-hidden" style={{ transitionDelay: "150ms" }}>
          {sent ? (
            <div className="glass rounded-xl p-12 text-center h-full flex flex-col items-center justify-center gap-4">
              <div className="w-16 h-16 rounded-full bg-violet-500/20 border border-violet-500/30 flex items-center justify-center mb-2">
                <Icon name="CheckIcon" size={28} className="text-violet-400" />
              </div>
              <h3 className="font-display font-bold text-xl text-white">Nachricht gesendet!</h3>
              <p className="text-white/45 text-sm">Ich melde mich so schnell wie möglich bei dir.</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="glass rounded-xl p-8 lg:p-10 space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className="block font-mono text-[10px] text-white/35 uppercase tracking-widest mb-2">Name</label>
                  <input
                    type="text"
                    required
                    placeholder="Dein Name"
                    className="form-input"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block font-mono text-[10px] text-white/35 uppercase tracking-widest mb-2">E-Mail</label>
                  <input
                    type="email"
                    required
                    placeholder="deine@email.ch"
                    className="form-input"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                  />
                </div>
              </div>

              <div>
                <label className="block font-mono text-[10px] text-white/35 uppercase tracking-widest mb-2">
                  Telefonnummer / WhatsApp <span className="text-white/20">(optional)</span>
                </label>
                <input
                  type="tel"
                  placeholder="+41 79 000 00 00"
                  className="form-input"
                  value={form.telefon}
                  onChange={(e) => setForm({ ...form, telefon: e.target.value })}
                />
              </div>

              <div>
                <label className="block font-mono text-[10px] text-white/35 uppercase tracking-widest mb-2">Was soll gemacht werden?</label>
                <select
                  className="form-input"
                  value={form.was}
                  onChange={(e) => setForm({ ...form, was: e.target.value })}
                >
                  <option value="" disabled>Bitte wählen...</option>
                  <option value="basic">Basic Build – 120 CHF</option>
                  <option value="setup">Setup Build – 180 CHF</option>
                  <option value="premium">Premium Build – 250 CHF</option>
                  <option value="budget">Budget PC Build – 100 CHF + Build-Paket</option>
                  <option value="addons">Nur Add-ons / Einzelleistung</option>
                  <option value="beratung">Sonstiges / Beratung</option>
                </select>
              </div>

              <div>
                <label className="block font-mono text-[10px] text-white/35 uppercase tracking-widest mb-2">Nachricht</label>
                <textarea
                  rows={5}
                  placeholder="Beschreibe kurz, was du dir vorstellst..."
                  className="form-input resize-none"
                  value={form.nachricht}
                  onChange={(e) => setForm({ ...form, nachricht: e.target.value })}
                />
              </div>

              {error && (
                <div className="flex items-center gap-2 p-3 rounded-lg border border-red-500/20 bg-red-500/[0.06]">
                  <span className="text-xs text-red-400">{error}</span>
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>
                    <svg className="animate-spin" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
                    </svg>
                    Wird gesendet...
                  </>
                ) : (
                  <>
                    Nachricht senden
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <path d="M5 12h14M12 5l7 7-7 7" />
                    </svg>
                  </>
                )}
              </button>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}